import { Controller } from "@hotwired/stimulus";
import { getSanitationRouteBoundToDocument } from "../db/sanitationRoutes.js";
import { getRouteDatapoints } from "../db/routeDatapoints.js";
import { getRouteLocations } from "../db/routeLocations.js";
import {
  mapToServerShapeRouteDatapoints,
  totalTreesForUpload,
} from "./sanitation_routes/upload_button_controller.js";
import { DebugLogger } from "../db/debugLogger.js";

// Downloads a route's local data as a JSON backup file. The file lands in the
// device's Downloads folder — OUTSIDE the browser's evictable origin storage —
// so a monitor can safeguard a route when upload fails or storage is at risk.
// Server-side recovery: `rake sanitation_routes:import_backup[file,user_id]`.
// Photos are not included (only their queue metadata survives locally anyway
// once storage pressure hits).
export default class extends Controller {
  static values = { errorMessage: String };

  async export(event) {
    event.preventDefault();

    try {
      const route = await this.resolveRoute(event);
      if (!route) throw new Error("No sanitation route found locally.");

      const routeDatapoints = await getRouteDatapoints(route.id);
      const routeLocations = await getRouteLocations(route.id);

      const backup = {
        version: 1,
        exported_at: new Date().toISOString(),
        total_trees_in_route: totalTreesForUpload(route, routeDatapoints),
        sanitation_route: route,
        route_datapoints: mapToServerShapeRouteDatapoints(routeDatapoints),
        route_locations: routeLocations,
      };

      this.downloadJson(backup, this.backupFileName(route));
      DebugLogger.info("Route backup exported", {
        routeId: route.id,
        datapointCount: routeDatapoints.length,
      });
    } catch (error) {
      DebugLogger.error("Route backup export failed", {
        error: error?.message,
      });
      alert(
        this.errorMessageValue ||
          "No se pudo exportar el respaldo. Inténtalo de nuevo.",
      );
    }
  }

  async resolveRoute(event) {
    const routeIdElement = event.target.closest("[data-sanitation-route-id]");
    const explicitRouteId = routeIdElement
      ? Number(routeIdElement.dataset.sanitationRouteId)
      : null;

    return getSanitationRouteBoundToDocument({ explicitRouteId });
  }

  backupFileName(route) {
    const date = new Date().toISOString().slice(0, 10);
    return `ruta-respaldo-${route.id}-${date}.json`;
  }

  downloadJson(payload, fileName) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
}
